import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useCallStore } from '../store/useCallStore';
import { useAuthStore } from '../store/useAuthStore';
import { fetchCallHistory, isWarming } from '../api/callhistory';
import type { WarmingProgress } from '../api/callhistory';

// Lade-Logik der Anrufliste (Fetch, Warming-Retry, calls_update-Push, 60s-Fallback)
// — genutzt von der Hauptansicht und der Mini-Ansicht (#/mini bzw. PiP-Fenster).

export function useCallHistoryLoader() {
  const { filter, setCalls, setLoading, setError } = useCallStore();
  const user = useAuthStore((s) => s.user);
  const [warmingTick, setWarmingTick] = useState(0);
  const [warmingProgress, setWarmingProgress] = useState<WarmingProgress | null>(null);

  // Suche entprellt an den Server schicken (Volltext über den ganzen Zeitraum)
  const [debouncedSearch, setDebouncedSearch] = useState(filter.search);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(filter.search), 400);
    return () => clearTimeout(id);
  }, [filter.search]);

  const loadCallHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCallHistory({
        period: filter.period, count: filter.count, fields: filter.fields,
        missedOnly: filter.missedOnly, search: debouncedSearch,
        viewAsCompanyId: filter.viewAsCompanyId,
      });
      if (isWarming(data)) {
        // Großer Zeitraum wird noch vom SBC geladen — Teilstand anzeigen und
        // automatisch nachfragen (Effekt unten).
        setCalls(data.calls ?? []);
        setWarmingProgress(data.progress ?? null);
        setWarmingTick((t) => t + 1);
      } else {
        setWarmingTick(0);
        setWarmingProgress(null);
        setCalls(data);
      }
    } catch (e: unknown) {
      setWarmingTick(0);
      setWarmingProgress(null);
      if (axios.isAxiosError(e)) {
        setError(e.response?.data?.error ?? e.message ?? 'Fehler beim Laden der Anrufliste.');
      } else {
        setError('Fehler beim Laden der Anrufliste.');
      }
    } finally {
      setLoading(false);
    }
  }, [filter.period, filter.count, filter.fields, filter.missedOnly, filter.viewAsCompanyId, debouncedSearch, setCalls, setLoading, setError]);

  useEffect(() => {
    if (user) loadCallHistory();
  }, [loadCallHistory, user]);

  // Stilles Sicherheitsnetz: Updates kommen per WebSocket (calls_update); dieser
  // Fallback-Poll fängt nur Sonderfälle ab (eigene Spalten-Variante, WS-Lücken).
  useEffect(() => {
    if (!user) return;
    const id = setInterval(loadCallHistory, 60_000);
    return () => clearInterval(id);
  }, [loadCallHistory, user]);

  // Solange das Backend den Zeitraum aufbaut (202), alle 10 s erneut anfragen.
  useEffect(() => {
    if (warmingTick === 0 || !user) return;
    const id = setTimeout(loadCallHistory, 10_000);
    return () => clearTimeout(id);
  }, [warmingTick, loadCallHistory, user]);

  // WS-Signal „neue Anrufe": gezielt nachladen, wenn es den angezeigten Zeitraum
  // betrifft (kurz entprellt; die Antwort kommt aus dem warmen Server-Cache).
  const callsSignal = useCallStore((s) => s.callsUpdateSignal);
  useEffect(() => {
    if (!user || !callsSignal) return;
    if (callsSignal.period !== '*' && callsSignal.period !== filter.period) return;
    const id = setTimeout(loadCallHistory, 300);
    return () => clearTimeout(id);
  }, [callsSignal, filter.period, loadCallHistory, user]);

  return { loadCallHistory, warmingTick, warmingProgress };
}
